// This file is used for the Live Match Commentary Block.
import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { SocketContext } from "../../context/SocketContext";
import MatchService from "../../services/matchService"; // Added to fetch summary commentary as fallback
import CommentaryValidationBoundary from './CommentaryValidationBoundary';
import { fanOutNarrative, getCommentaryKey } from '../../utils/fanOutNarrative';
import { eventIcons, formatCommentaryMinute } from '../../utils/commentaryHelpers';
import {
    getCommentaryDripDelayMs,
    shouldFlushCommentaryBurstImmediately
} from '../../utils/commentaryPacing';
import {
    FEED_TIER,
    buildPeriodBandIdentitySet,
    classifyFeedEvent,
    getFeedEventGroupIdentity,
    getFeedEventIdentity,
    isAbsorbedFeedEvent
} from '../../utils/matchFeed';
import { cleanMatchText, formatPlayerName } from '../../utils/playerName';

// A full match can easily produce 300+ commentary events. A 100-row cap hides
// early goals after refresh, making the final score look wrong.
const MAX_VISIBLE_COMMENTARY_EVENTS = 1000;

function compareCommentaryEventsNewestFirst(a, b) {
    if (a?.sequence_number != null && b?.sequence_number != null) {
        return b.sequence_number - a.sequence_number;
    }

    const aTimestamp = a?.timestamp ? new Date(a.timestamp).getTime() : NaN;
    const bTimestamp = b?.timestamp ? new Date(b.timestamp).getTime() : NaN;
    if (Number.isFinite(aTimestamp) && Number.isFinite(bTimestamp) && aTimestamp !== bTimestamp) {
        return bTimestamp - aTimestamp;
    }

    return (b?.minute || 0) - (a?.minute || 0);
}

// ----- Helper functions (module-level) -----
export function highlightEmphasisHelper(text, emphasisArray) {
    if (!text) return '';
    let highlightedText = text;
    (emphasisArray || []).forEach(term => {
        if (term) {
            const regex = new RegExp(`\\b(${term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')})\\b`, 'gi');
            highlightedText = highlightedText.replace(regex, `<strong>$1</strong>`);
        }
    });
    return highlightedText;
}

const EVENT_LABELS = {
    SAVE: 'SAVE',
    PENALTY_SAVE: 'PENALTY SAVED',
    PENALTY_MISS: 'PENALTY MISSED',
    PENALTY_TAKEN: 'PENALTY',
    PENALTY_AWARDED: 'PENALTY AWARDED',
    YELLOW_CARD: 'YELLOW CARD',
    RED_CARD: 'RED CARD',
    FOUL: 'FOUL',
    FOUL_WARNING: 'REFEREE WARNING',
    INJURY: 'INJURY',
    SUBSTITUTION: 'SUBSTITUTION',
    TACTIC_CHANGE: 'TACTIC CHANGE',
    VAR_CHECK: 'VAR CHECK',
    VAR_GOAL_DISALLOWED: 'VAR: GOAL DISALLOWED',
    VAR_GOAL_CONFIRMED: 'VAR: GOAL CONFIRMED'
};

const BAND_LABELS = {
    KICKOFF: 'KICK-OFF',
    HALF_TIME: 'HALF-TIME',
    FULL_TIME: 'FULL-TIME'
};

const getTeamDisplayName = (team, fallback) => (
    team?.shortTitle || team?.shortName || team?.name || fallback
);

export const buildBandLabel = (event, teamOne, teamTwo, finalScore) => {
    const type = event?.event_type || event?.type;
    const base = BAND_LABELS[type] || '';
    const score = event?.score || (type === 'FULL_TIME' ? finalScore : null);

    if ((type === 'HALF_TIME' || type === 'FULL_TIME') && score &&
        typeof score.home === 'number' && typeof score.away === 'number') {
        const home = getTeamDisplayName(teamOne, 'Home');
        const away = getTeamDisplayName(teamTwo, 'Away');
        return `${base} | ${home} ${score.home} : ${score.away} ${away}`;
    }

    return base;
};

const getGoalScoreText = (event) => {
    const score = event?.score;
    if (!score || typeof score.home !== 'number' || typeof score.away !== 'number') return null;
    return `${score.home} - ${score.away}`;
};

const SAVED_GOAL_EVENT_TYPES = new Set(['GOAL', 'GOAL_NARRATIVE', 'PENALTY_GOAL']);

const stampSavedCommentaryScores = (events, shouldDeferScore = () => false) => {
    const runningScore = { home: 0, away: 0 };
    const countedGoalSides = [];

    return events.map((event) => {
        const stampedEvent = { ...event };
        const eventTypes = [event?.event_type, event?.type];
        const sideValue = String(event?.team_context || '').toLowerCase();
        const side = sideValue === 'home' || sideValue === 'away' ? sideValue : null;

        if (eventTypes.some((eventType) => SAVED_GOAL_EVENT_TYPES.has(eventType))) {
            if (side) {
                runningScore[side] += 1;
                countedGoalSides.push(side);
                if (event.score == null && !shouldDeferScore(event)) {
                    stampedEvent.score = { ...runningScore };
                }
            }
            return stampedEvent;
        }

        if (eventTypes.includes('VAR_GOAL_DISALLOWED')) {
            let disallowedSide = side;
            if (disallowedSide) {
                const countedIndex = countedGoalSides.lastIndexOf(disallowedSide);
                if (countedIndex >= 0) countedGoalSides.splice(countedIndex, 1);
            } else {
                disallowedSide = countedGoalSides.pop() || null;
            }
            if (disallowedSide) {
                runningScore[disallowedSide] = Math.max(0, runningScore[disallowedSide] - 1);
            }
            return stampedEvent;
        }

        if (
            event.score == null &&
            !shouldDeferScore(event) &&
            (eventTypes.includes('HALF_TIME') || eventTypes.includes('FULL_TIME'))
        ) {
            stampedEvent.score = { ...runningScore };
        }

        return stampedEvent;
    });
};

// =============================================
// Extracted Comments component to top level to
// ensure identity stability between renders.
// =============================================
const Comments = React.memo(function Comments({ rows, teamOne, teamTwo, finalScore }) {
    // Track previously seen keys so only genuinely new rows animate in.
    const prevKeysRef = React.useRef(new Set());

    if (!rows || rows.length === 0) {
        return (
            <div className="mx-feed-waiting" data-testid="commentary-waiting">
                Waiting for live match commentary...
            </div>
        );
    }

    return (
        <>
            {rows.map((row) => {
                const isNew = !row.event?._isHistory && !prevKeysRef.current.has(row.key);
                prevKeysRef.current.add(row.key);
                return (
                    <CommentaryRow
                        key={row.key}
                        event={row.event}
                        isBand={row.isBand}
                        teamOne={teamOne}
                        teamTwo={teamTwo}
                        finalScore={finalScore}
                        isNew={isNew}
                    />
                );
            })}
        </>
    );
});

function DescriptorChips({ descriptors }) {
    if (!descriptors || descriptors.length === 0) return null;
    return (
        <div className="mx-desc-chips">
            {descriptors.map((descriptor) => (
                <span className="mx-desc-chip" key={descriptor}>{cleanMatchText(descriptor)}</span>
            ))}
        </div>
    );
}

// Only the event types that actually ship an icon get a badge. An empty badge
// reads as a broken image, so routine rows simply do without one.
function EventIcon({ eventType }) {
    const iconSrc = eventIcons[eventType];
    if (!iconSrc) return null;

    return (
        <span className="mx-evt__icon" aria-hidden="true">
            <img src={iconSrc} alt="" />
        </span>
    );
}

// ----- Memoised single commentary row (top-level) -----
export const CommentaryRow = React.memo(function CommentaryRow({ event, teamOne, teamTwo, finalScore, isNew, isBand = false }) {
    const classification = classifyFeedEvent(event);
    const { type, tier, isKeyMoment, side, subType, accent, descriptors } = classification;

    if (isBand) {
        return (
            <div className={`mx-feed-row mx-feed-row--band mx-feed-row--key${isNew ? ' mx-feed-enter' : ''}`}>
                <div className="mx-band">
                    <span>{buildBandLabel(event, teamOne, teamTwo, finalScore)}</span>
                </div>
            </div>
        );
    }

    const emphasis = Array.isArray(event.emphasis)
        ? event.emphasis.map((term) => cleanMatchText(term))
        : event.emphasis;
    const messageHtml = highlightEmphasisHelper(cleanMatchText(event.message_text), emphasis);
    const isGoalHero = tier === FEED_TIER.GOAL && (subType === 'outcome' || !subType);
    const isGoalPart = tier === FEED_TIER.GOAL && !isGoalHero;
    const scoreText = isGoalHero ? getGoalScoreText(event) : null;
    const assist = isGoalHero ? formatPlayerName(event.secondary_player_name) : '';

    const rowClasses = [
        'mx-feed-row',
        `mx-feed-row--${side || 'system'}`,
        `mx-feed-row--tier-${tier}`,
        isKeyMoment ? 'mx-feed-row--key' : null,
        isNew ? 'mx-feed-enter' : null
    ].filter(Boolean).join(' ');

    const boxClasses = [
        'mx-evt',
        isGoalHero ? 'mx-evt--goal-hero' : null,
        isGoalPart ? 'mx-evt--medium mx-evt--goal-part' : null,
        !isGoalHero && !isGoalPart && tier === FEED_TIER.QUIET ? 'mx-evt--quiet' : null,
        !isGoalHero && !isGoalPart && tier === FEED_TIER.MEDIUM ? 'mx-evt--medium' : null,
        !isGoalHero && !isGoalPart && tier === FEED_TIER.KEY ? 'mx-evt--key' : null,
        side && !isGoalHero ? `mx-evt--${side}` : null,
        subType ? `mx-evt--${subType}` : null,
        accent && !isGoalHero ? `mx-evt--accent-${accent}` : null
    ].filter(Boolean).join(' ');

    // The label only rides the action part so a three part narrative does not
    // shout its own name three times in a row.
    const showEventLabel = tier === FEED_TIER.KEY && (subType === 'action' || !subType);
    const eventLabel = EVENT_LABELS[type];
    const isInlineBox = boxClasses.includes('mx-evt--quiet') || boxClasses.includes('mx-evt--medium');

    return (
        <div className={rowClasses} data-event-type={type}>
            <div className="mx-feed-min">{formatCommentaryMinute(event)}</div>
            <div className="mx-feed-body">
                <div className={boxClasses}>
                    {isGoalHero && (
                        <div className="mx-evt__goal-band">
                            <span className="mx-evt__goal-label">GOAL!</span>
                            {scoreText && <span className="mx-evt__goal-score">{scoreText}</span>}
                        </div>
                    )}
                    {showEventLabel && eventLabel && (
                        <div className="mx-evt__who">{eventLabel}</div>
                    )}
                    {isInlineBox && <EventIcon eventType={type} />}
                    <div className="mx-evt__content">
                        <div
                            className="message"
                            dangerouslySetInnerHTML={{ __html: messageHtml }}
                        />
                        {assist && <div className="mx-evt__assist">Assist: {assist}</div>}
                        <DescriptorChips descriptors={descriptors} />
                    </div>
                </div>
            </div>
        </div>
    );
}, (prev, next) => (
    getCommentaryKey(prev.event) === getCommentaryKey(next.event) &&
    prev.isNew === next.isNew &&
    prev.isBand === next.isBand &&
    (!next.isBand || (
        prev.finalScore?.home === next.finalScore?.home &&
        prev.finalScore?.away === next.finalScore?.away
    ))
));

// ----- End CommentaryRow -----

const CommentariesBlock = ({
    matchId,
    matchStatus,
    teamOne,
    teamTwo,
    finalScore = null,
    onEventsChange
}) => {
    const [commentaryEvents, setCommentaryEvents] = useState([]); // Stores all commentary events
    // Highest minute aired so far. A ref, not state: it is only read inside
    // socket handlers, and state would be stale in callbacks captured at
    // subscribe time.
    const latestMinuteRef = useRef(0);

    // Use ref to avoid re-renders on every heartbeat update
    const lastHeartbeatRef = useRef(Date.now());

    // Track all commentary keys that have been displayed or queued to avoid duplicates
    const seenKeysRef = useRef(new Set());
    const commentaryEventsRef = useRef([]);

    // Saved commentary becomes authoritative once a finished match hydrates.
    const hydrationAppliedRef = useRef(false);
    const hydrationRetryTimerRef = useRef(null);
    const hydrationRequestRef = useRef(null);
    const hydrationGenerationRef = useRef(0);
    const resetMatchIdRef = useRef(null);

    // ==== QUEUE-AND-DRIP MECHANISM (sequential reveal) ====
    // Stores commentary events that have arrived but are not yet visible.
    const eventQueueRef = useRef([]);
    // Tracks the active setTimeout; null when queue is idle.
    const dripTimerRef = useRef(null);

    const mergeIntoVisibleEvents = React.useCallback((eventsToShow) => {
        // Every aired event passes through here, so this is where the latest
        // minute tracker is kept up to date. Max, not last, so out-of-order
        // rows cannot rewind it.
        for (const ev of eventsToShow) {
            if (typeof ev?.minute === 'number' && ev.minute > latestMinuteRef.current) {
                latestMinuteRef.current = ev.minute;
            }
        }
        setCommentaryEvents(prevEvents => {
            // Merge and cap length; we already guard duplicates in enqueueEvents.
            const combined = eventsToShow
                .concat(prevEvents)
                .sort(compareCommentaryEventsNewestFirst);
            return combined.slice(0, MAX_VISIBLE_COMMENTARY_EVENTS);
        });
    }, []);

    // Reveals queued events one at a time so live rows animate in sequence.
    // Large backlogs (reconnect, resync, first paint of a match in progress)
    // are flushed in one go instead of dripping for minutes.
    const flushNextEvent = React.useCallback(() => {
        if (eventQueueRef.current.length === 0) {
            dripTimerRef.current = null;
            return;
        }

        if (shouldFlushCommentaryBurstImmediately(eventQueueRef.current.length)) {
            const everything = eventQueueRef.current.splice(0, eventQueueRef.current.length);
            mergeIntoVisibleEvents(everything);
            dripTimerRef.current = null;
            return;
        }

        const nextEvent = eventQueueRef.current.shift();
        mergeIntoVisibleEvents([nextEvent]);

        if (eventQueueRef.current.length === 0) {
            dripTimerRef.current = null;
            return;
        }

        const delayMs = getCommentaryDripDelayMs(nextEvent, eventQueueRef.current.length);
        dripTimerRef.current = setTimeout(flushNextEvent, delayMs);
    }, [mergeIntoVisibleEvents]);

    // Adds events to the queue and starts the drip if idle.
    const enqueueEvents = React.useCallback((eventsArr) => {
        if (!eventsArr || eventsArr.length === 0) return;

        const fresh = [];
        for (const ev of eventsArr) {
            const key = getCommentaryKey(ev);
            if (seenKeysRef.current.has(key)) {
                continue;
            }
            seenKeysRef.current.add(key);
            fresh.push(ev);
        }

        if (fresh.length === 0) return; // nothing new

        eventQueueRef.current.push(...fresh);

        if (!dripTimerRef.current) {
            flushNextEvent();
        }
    }, [flushNextEvent]);

    // Track socket subscription to avoid repeated subscribing
    const hasSubscribedRef = useRef(false);

    const socketCtx = useContext(SocketContext);

    const heartbeatIntervalRef = useRef(null);

    // Clear commentary events ONLY when the match ID actually changes (switching to a different match)
    useEffect(() => {
        if (resetMatchIdRef.current === matchId) return;
        resetMatchIdRef.current = matchId;
        setCommentaryEvents([]);
        commentaryEventsRef.current = [];
        seenKeysRef.current.clear();
        hydrationAppliedRef.current = false;
        hydrationGenerationRef.current += 1;
        hydrationRequestRef.current = null;
        if (hydrationRetryTimerRef.current) {
            clearTimeout(hydrationRetryTimerRef.current);
            hydrationRetryTimerRef.current = null;
        }
        // Reset the reveal queue when switching matches
        eventQueueRef.current = [];
        if (dripTimerRef.current) {
            clearTimeout(dripTimerRef.current);
            dripTimerRef.current = null;
        }
        latestMinuteRef.current = 0;
        lastHeartbeatRef.current = Date.now();
    }, [matchId]);

    // Report the visible feed upwards so the scoreboard can derive scorers,
    // timeline pips and the live minute from the same de-duplicated stream.
    useEffect(() => {
        commentaryEventsRef.current = commentaryEvents;
        if (typeof onEventsChange === 'function') {
            onEventsChange(commentaryEvents);
        }
    }, [commentaryEvents, onEventsChange]);

    // Finished matches hydrate from saved commentary because it is the complete,
    // authoritative history, including events missed while this page was away.
    useEffect(() => {
        const isFinished = ['played', 'finished', 'completed', 'ended'].includes(matchStatus);
        if (!matchId || !isFinished || hydrationAppliedRef.current) return undefined;

        let didCancel = false;
        const generation = hydrationGenerationRef.current + 1;
        hydrationGenerationRef.current = generation;
        const retryDelays = [1000, 2000, 4000, 8000, 15000, 30000, 60000];
        let retryIndex = 0;

        const isCurrentRun = () => (
            !didCancel &&
            hydrationGenerationRef.current === generation &&
            !hydrationAppliedRef.current
        );

        const requestSummary = () => {
            const currentRequest = hydrationRequestRef.current;
            if (currentRequest?.matchId === matchId) {
                return currentRequest.promise;
            }

            const request = MatchService.getMatchSummary(matchId);
            const promise = Promise.resolve(request).finally(() => {
                if (hydrationRequestRef.current?.promise === promise) {
                    hydrationRequestRef.current = null;
                }
            });
            hydrationRequestRef.current = { matchId, promise };
            return promise;
        };

        const scheduleRetry = () => {
            if (!isCurrentRun() || retryIndex >= retryDelays.length) return;
            const delay = retryDelays[retryIndex];
            retryIndex += 1;
            hydrationRetryTimerRef.current = setTimeout(() => {
                hydrationRetryTimerRef.current = null;
                fetchSavedCommentary();
            }, delay);
        };

        async function fetchSavedCommentary() {
            let summaryData;
            try {
                summaryData = await requestSummary();
            } catch (err) {
                if (isCurrentRun()) {
                    console.error(`[CommentariesBlock] Failed to fetch saved commentary for match ${matchId}:`, err);
                }
                scheduleRetry();
                return;
            }

            if (!isCurrentRun()) return;
            if (summaryData == null) {
                scheduleRetry();
                return;
            }

            const savedCommentary = Array.isArray(summaryData.commentary)
                ? summaryData.commentary
                : [];
            if (savedCommentary.length === 0) return;

            const liveCandidates = commentaryEventsRef.current.concat(eventQueueRef.current);
            const candidatesByGroup = new Map();
            liveCandidates.forEach((event) => {
                const groupIdentity = getFeedEventGroupIdentity(event);
                const candidates = candidatesByGroup.get(groupIdentity) || [];
                candidates.push(event);
                candidatesByGroup.set(groupIdentity, candidates);
            });
            const usedDonors = new Set();
            const hasFinalScore = finalScore &&
                typeof finalScore.home === 'number' &&
                typeof finalScore.away === 'number';
            const shouldDeferScore = (event) => {
                const eventTypes = [event?.event_type, event?.type];
                if (hasFinalScore && eventTypes.includes('FULL_TIME')) return true;
                const candidates = candidatesByGroup.get(getFeedEventGroupIdentity(event)) || [];
                return candidates.some((candidate) => candidate.score != null);
            };

            const replacement = fanOutNarrative(stampSavedCommentaryScores(savedCommentary, shouldDeferScore).map((event, index) => ({
                ...event,
                _history_index: index
            }))).map((event) => {
                const candidates = candidatesByGroup.get(getFeedEventGroupIdentity(event)) || [];
                const exactSubTypeCandidate = event.sub_type
                    ? candidates.find((candidate) => (
                        !usedDonors.has(candidate) &&
                        candidate.sub_type &&
                        candidate.sub_type === event.sub_type
                    ))
                    : null;
                const donor = exactSubTypeCandidate || candidates.find((candidate) => !usedDonors.has(candidate));
                const hydratedEvent = { ...event, _isHistory: true };

                if (donor) {
                    usedDonors.add(donor);
                    ['score', 'formal_minute', 'timestamp', 'sequence_number'].forEach((field) => {
                        if (hydratedEvent[field] == null && donor[field] != null) {
                            hydratedEvent[field] = donor[field];
                        }
                    });
                }

                return hydratedEvent;
            }).sort(compareCommentaryEventsNewestFirst).slice(0, MAX_VISIBLE_COMMENTARY_EVENTS);

            hydrationAppliedRef.current = true;
            if (hydrationRetryTimerRef.current) {
                clearTimeout(hydrationRetryTimerRef.current);
                hydrationRetryTimerRef.current = null;
            }
            if (dripTimerRef.current) {
                clearTimeout(dripTimerRef.current);
                dripTimerRef.current = null;
            }
            eventQueueRef.current = [];
            seenKeysRef.current.clear();
            replacement.forEach((event) => {
                seenKeysRef.current.add(getCommentaryKey(event));
            });
            // Hydration replaces the feed wholesale, bypassing
            // mergeIntoVisibleEvents, so update the minute tracker here too.
            for (const ev of replacement) {
                if (typeof ev?.minute === 'number' && ev.minute > latestMinuteRef.current) {
                    latestMinuteRef.current = ev.minute;
                }
            }
            commentaryEventsRef.current = replacement;
            setCommentaryEvents(replacement);
        }

        fetchSavedCommentary();

        return () => {
            didCancel = true;
            if (hydrationGenerationRef.current === generation) {
                hydrationGenerationRef.current += 1;
            }
            if (hydrationRetryTimerRef.current) {
                clearTimeout(hydrationRetryTimerRef.current);
                hydrationRetryTimerRef.current = null;
            }
        };
    }, [matchId, matchStatus, finalScore]);

    useEffect(() => {
        let unsubscribeCommentary = () => {};
        let unsubscribeStateUpdate = () => {};
        let unsubscribeCommentaryError = () => {};
        let unsubscribeBusyUpdate = () => {};
        let unsubscribeCommandError = () => {};

        if (socketCtx && typeof socketCtx.subscribe === 'function' && socketCtx.status === 'connected' && !hasSubscribedRef.current) {
            hasSubscribedRef.current = true;

            unsubscribeCommentary = socketCtx.subscribe('commentary_update', (data) => {
                try {
                    if (hydrationAppliedRef.current) return;

                    // Extract payload from wrapped format {type: 'commentary_update', payload: {...}}
                    const events = data.payload || data;

                    const normalisedEvents = Array.isArray(events) ? events : [events];

                    // Validate and process events
                    const validEvents = normalisedEvents.filter(event => {
                        const isValid = event.event_type && (event.message_text || event.narrative) && typeof event.minute === 'number';
                        if (!isValid) {
                            console.warn('[CommentariesBlock] Invalid commentary event filtered:', event);
                        }
                        return isValid;
                    });

                    // Fan out narrative events into sub-events
                    const fannedOutEvents = fanOutNarrative(validEvents);

                    if (fannedOutEvents.length > 0) {
                        // Sort by sequence number if available, otherwise by minute
                        const sortedEvents = fannedOutEvents.sort(compareCommentaryEventsNewestFirst);

                        const relevantEvents = sortedEvents.filter(event => {
                            const evMatchId = event.match_id ?? event.matchId;
                            return !!evMatchId && evMatchId === matchId;
                        });
                        if (relevantEvents.length > 0) {
                            // Queue events so they are displayed one-by-one according to delay rules.
                            enqueueEvents(relevantEvents);

                            lastHeartbeatRef.current = Date.now();
                        }
                    }

                } catch (error) {
                    console.error('[CommentariesBlock] Error processing commentary update:', error);
                }
            });

            // Engine state updates: only used for heartbeat tracking
            unsubscribeStateUpdate = socketCtx.subscribe('engine_state_update', () => {
                // Lightweight heartbeat update only (does not trigger render)
                lastHeartbeatRef.current = Date.now();
            });

            // Handle commentary error events for graceful degradation
            unsubscribeCommentaryError = socketCtx.subscribe('commentary_error', (errorData) => {
                console.error('[CommentariesBlock] Commentary error received:', errorData);

                setCommentaryEvents(prevEvents => [{
                    event_type: 'SYSTEM_MESSAGE',
                    minute: latestMinuteRef.current,
                    message_text: 'Commentary temporarily unavailable',
                    emphasis: ['system-message'],
                    timestamp: new Date().toISOString()
                }, ...prevEvents]);
            });

            // Placeholder removed; keep subscription for future use but do nothing.
            unsubscribeBusyUpdate = socketCtx.subscribe('busy_update', () => {});

            // Handle command_error events from engine (when commands are rejected during busy periods)
            unsubscribeCommandError = socketCtx.subscribe('command_error', (data) => {
                const errorData = data.payload || data;

                // Show user-friendly error message as a system commentary (except transient engine_busy)
                if (errorData && errorData.message && errorData.reason !== 'engine_busy') {
                    const latestMinute = latestMinuteRef.current;
                    const systemMessage = {
                        event_type: 'SYSTEM_MESSAGE',
                        minute: latestMinute,
                        formal_minute: `${latestMinute}'`,
                        message_text: errorData.message,
                        emphasis: ['system-error'],
                        timestamp: new Date().toISOString(),
                        unique_id: `command-error-${Date.now()}`
                    };

                    setCommentaryEvents(prevEvents => [systemMessage, ...prevEvents]);
                }
            });
        }

        return () => {
            unsubscribeCommentary();
            unsubscribeStateUpdate();
            unsubscribeCommentaryError();
            unsubscribeBusyUpdate();
            unsubscribeCommandError();
            // Clear any pending drip timer and flush the queue on unmount
            if (dripTimerRef.current) {
                clearTimeout(dripTimerRef.current);
                dripTimerRef.current = null;
            }
            eventQueueRef.current = [];
            seenKeysRef.current.clear();
            hasSubscribedRef.current = false;
        };
    }, [socketCtx, matchId]);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (heartbeatIntervalRef.current) {
                clearInterval(heartbeatIntervalRef.current);
            }
            if (dripTimerRef.current) {
                clearTimeout(dripTimerRef.current);
                dripTimerRef.current = null;
            }
            eventQueueRef.current = [];
            seenKeysRef.current.clear();
        };
    }, []);

    // Rows are derived, never stored: SCORE_UPDATE is absorbed by the goal hero
    // card and only the opening kick-off of each half becomes a period band.
    const feedRows = useMemo(() => {
        const bandIdentities = buildPeriodBandIdentitySet(commentaryEvents);

        return commentaryEvents
            .filter((event) => !isAbsorbedFeedEvent(event))
            .map((event) => ({
                key: getCommentaryKey(event),
                event,
                isBand: bandIdentities.has(getFeedEventIdentity(event)) && !event.sub_type
            }));
    }, [commentaryEvents]);

    return (
      <CommentaryValidationBoundary>
        <div id="commentaries_block" className="mx-feed" data-testid="match-feed" style={{ overflowAnchor: 'none' }}>
            <div className="mx-feed-list">
                <Comments
                    rows={feedRows}
                    teamOne={teamOne}
                    teamTwo={teamTwo}
                    finalScore={finalScore}
                />
            </div>
        </div>
      </CommentaryValidationBoundary>
    );
}

export default React.memo(CommentariesBlock);
